import {
  CustomFieldEditors,
  ImplicitPermissions,
  ImplicitPermissionsProps,
  ImplicitRole,
  NameDescription,
} from './types'

export const useImplicitPermissions = ({
  user: {
    id: userId,
    casRoutingGroups,
    dsDisciplines,
    rfiResponseDisciplines,
    ejTypes,
    fdcApprovalGroups,
    fdcConcurrenceGroups,
    roles,
  },
  customFields,
}: ImplicitPermissionsProps) => {
  const customFieldEditorsMap = (customFields ?? []).reduce(
    (editorsMap, { id, name, description, customFieldToClassTypes }) => {
      editorsMap.set(id, {
        name,
        description,
        classTypes: customFieldToClassTypes.map(
          ({ classType, users, roles }) => ({
            classType,
            userIds: users.map(({ id }) => id),
            roleIds: roles.map(({ id }) => id),
          })
        ),
      })
      return editorsMap
    },
    new Map<string, CustomFieldEditors>()
  )
  // get names and descriptions of groups assigned by role
  const roleApprovalGroups =
    roles?.flatMap(
      ({ fdcApprovalGroups }) =>
        fdcApprovalGroups?.map(({ id, name, description }) => ({
          id,
          name,
          description,
        })) ?? []
    ) ?? []
  const allApprovalGroupsMap = new Map<string, NameDescription>()
  for (let { id, ...rest } of [
    ...roleApprovalGroups,
    ...fdcApprovalGroups.map(({ id, name, description }) => ({
      id,
      name,
      description,
    })),
  ]) {
    allApprovalGroupsMap.set(id, rest)
  }
  const roleConcurrenceGroups =
    roles?.flatMap(
      ({ fdcConcurrenceGroups }) =>
        fdcConcurrenceGroups?.map(({ id, name, description }) => ({
          id,
          name,
          description,
        })) ?? []
    ) ?? []
  const allConcurrenceGroupsMap = new Map<string, NameDescription>()
  for (let { id, ...rest } of [
    ...roleConcurrenceGroups,
    ...fdcConcurrenceGroups.map(({ id, name, description }) => ({
      id,
      name,
      description,
    })),
  ]) {
    allConcurrenceGroupsMap.set(id, rest)
  }

  // add names and descriptions of groups/disciplines user is associated with
  const implicitPermissions: ImplicitPermissions = {
    implicitRoles: [],
    dsDisciplines: dsDisciplines.map(({ name, description }) => ({
      name,
      description,
    })),
    casRoutingGroups: casRoutingGroups.map(({ name, description }) => ({
      name,
      description,
    })),
    rfiResponseDisciplines: rfiResponseDisciplines.map(
      ({ name, description }) => ({ name, description })
    ),
    ejTypes: ejTypes.map(({ name }) => ({ name })),
    fdcApprovalGroups: [...allApprovalGroupsMap.values()],
    fdcConcurrenceGroups: [...allConcurrenceGroupsMap.values()],
    customFields: new Map<string, NameDescription[]>(),
  }

  // add custom field name/description based on editors map
  const userRoleIds = roles?.map(({ id }) => id)
  customFieldEditorsMap.forEach(({ name, description, classTypes }) => {
    classTypes.forEach(({ classType, userIds, roleIds }) => {
      // check userIds and roles for permission
      if (
        (!userIds.length && !roleIds.length) ||
        userIds.includes(userId) ||
        roleIds.some((id) => userRoleIds.includes(id))
      ) {
        if (implicitPermissions.customFields.has(classType))
          implicitPermissions.customFields.set(classType, [
            ...implicitPermissions.customFields.get(classType)!,
            { name, description },
          ])
        else
          implicitPermissions.customFields.set(classType, [
            { name, description },
          ])
      }
    })
  })

  // add implicit roles
  if (!!casRoutingGroups.length)
    implicitPermissions.implicitRoles.push(ImplicitRole.CASApprover)
  if (!!dsDisciplines.length)
    implicitPermissions.implicitRoles.push(ImplicitRole.DSApprover)
  if (!!rfiResponseDisciplines.length)
    implicitPermissions.implicitRoles.push(ImplicitRole.RFIResponder)
  if (!!ejTypes.length)
    implicitPermissions.implicitRoles.push(ImplicitRole.EJIssuer)
  if (!!fdcApprovalGroups.length || !!roleApprovalGroups.length)
    implicitPermissions.implicitRoles.push(ImplicitRole.FDCApprover)
  if (!!fdcConcurrenceGroups.length || !!roleConcurrenceGroups.length)
    implicitPermissions.implicitRoles.push(ImplicitRole.FDCConcurrer)
  if (!!implicitPermissions.customFields.size)
    implicitPermissions.implicitRoles.push(ImplicitRole.CustomFieldEditor)

  return implicitPermissions
}
